import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { 
  CheckCircle, XCircle, Loader2, Ticket, ArrowLeft, 
  CreditCard, Smartphone, QrCode
} from 'lucide-react';
import api from '../../utils/api';

const BusPaymentSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const bookingId = searchParams.get('booking_id');
  
  const [loading, setLoading] = useState(true);
  const [booking, setBooking] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState(null);

  useEffect(() => {
    if (bookingId) {
      checkPaymentStatus();
    }
  }, [bookingId]);

  const checkPaymentStatus = async () => {
    try {
      const response = await api.get(`/bus/payments/status/${bookingId}`);
      setPaymentStatus(response.data);
      
      if (response.data.payment_status === 'paid') {
        // Fetch full booking details
        const bookingRes = await api.get('/bus/bookings/my');
        const found = bookingRes.data.bookings?.find(b => b.id === bookingId);
        setBooking(found);
      }
    } catch (error) {
      console.error('Error checking payment:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Verifying your payment...</p>
        </div>
      </div>
    );
  }

  const isPaid = paymentStatus?.payment_status === 'paid';

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 pb-20">
      {/* Header */}
      <div className={`${isPaid ? 'bg-gradient-to-r from-green-500 to-emerald-500' : 'bg-gradient-to-r from-yellow-500 to-orange-500'} text-white p-6`}>
        <div className="max-w-2xl mx-auto text-center">
          {isPaid ? (
            <>
              <CheckCircle className="h-16 w-16 mx-auto mb-4" />
              <h1 className="text-2xl font-bold mb-2">Payment Successful!</h1>
              <p className="text-green-100">Your bus ticket has been confirmed</p>
            </>
          ) : (
            <>
              <Loader2 className="h-16 w-16 mx-auto mb-4 animate-spin" />
              <h1 className="text-2xl font-bold mb-2">Payment Processing</h1>
              <p className="text-yellow-100">Your payment is being processed</p>
            </>
          )}
        </div>
      </div>

      <div className="max-w-2xl mx-auto p-4 -mt-4">
        <Card className="p-6">
          {isPaid && booking ? (
            <>
              {/* Ticket Code */}
              <div className="text-center mb-6 pb-6 border-b border-dashed">
                <Ticket className="h-8 w-8 text-blue-600 mx-auto mb-2" />
                <p className="text-sm text-gray-500">Ticket Code</p>
                <p className="text-3xl font-bold text-blue-600 tracking-wider">
                  {paymentStatus?.ticket_code}
                </p>
              </div>

              {/* QR Code */}
              {booking.qrCode && (
                <div className="text-center mb-6">
                  <img 
                    src={booking.qrCode} 
                    alt="Ticket QR Code" 
                    className="mx-auto w-40 h-40"
                  />
                  <p className="text-sm text-gray-500 mt-2">Show this QR code when boarding</p>
                </div>
              )}

              {/* Payment Info */}
              <div className="bg-green-50 rounded-lg p-4 mb-6">
                <div className="flex items-center gap-2 mb-2">
                  <CreditCard className="h-5 w-5 text-green-600" />
                  <span className="font-medium text-green-700">Payment Confirmed</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Amount Paid</span>
                  <span className="font-semibold text-green-700">K{paymentStatus?.amount}</span>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-3">
                <Button 
                  variant="outline"
                  className="flex-1"
                  onClick={() => navigate('/bus/tickets')}
                >
                  View My Tickets
                </Button>
                <Button 
                  className="flex-1 bg-blue-600 hover:bg-blue-700"
                  onClick={() => navigate('/bus')}
                >
                  Book Another Trip
                </Button>
              </div>
            </>
          ) : (
            <div className="text-center py-8">
              <Smartphone className="h-16 w-16 text-yellow-500 mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-2">Complete Your Payment</h3>
              <p className="text-gray-600 mb-6">
                Please complete the payment process in the payment window.
                This page will update automatically once payment is confirmed.
              </p>
              
              <Button 
                variant="outline"
                onClick={checkPaymentStatus}
                className="mb-4"
              >
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Check Payment Status
              </Button>
              
              <div className="mt-4">
                <Button 
                  variant="ghost"
                  onClick={() => navigate('/bus/tickets')}
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back to My Tickets
                </Button>
              </div>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

export default BusPaymentSuccess;
